import { type Component, type JSX } from "solid-js"
import { createStore, produce } from "solid-js/store"
import { AppContext } from "./context"
import type { AppActions, AppState } from "./types"

interface AppProviderProps {
	children: JSX.Element
}

const DEFAULT_STATE: AppState = {
	mode: "edit",
	scenarioMode: "default",
	editTab: "agents",
	isEditable: true,

	selectedAgentId: null,
	selectedViewportPinId: null,
	simSelectedAgentId: null,

	isAddingWaypoints: false,
	activeSegmentIdx: 0,

	showLabels: true,

	isMobile: false,
	isMobileMenuOpen: false,

	modals: {
		help: false,
		analysis: false,
		share: false,
		scenarioSelection: false,
		agentDetails: false,
		saveBeforeLoad: false,
		shareResult: false,
		welcome: false,
		error: false,
	},

	shareUrl: "",
	errorMessage: "",
}

/**
 * AppProvider - provides unified UI/navigation state to the component tree
 */
export const AppProvider: Component<AppProviderProps> = (props) => {
	const [state, setState] = createStore<AppState>(structuredClone(DEFAULT_STATE))

	const actions: AppActions = {
		// Navigation
		setMode: (mode) => setState("mode", mode),
		setScenarioMode: (mode) => setState("scenarioMode", mode),
		setEditTab: (tab) => setState("editTab", tab),
		setIsEditable: (v) => setState("isEditable", v),

		// Selection
		selectAgent: (id) => setState("selectedAgentId", id),
		selectViewportPin: (id) => setState("selectedViewportPinId", id),
		setSimSelectedAgentId: (id) => setState("simSelectedAgentId", id),

		// Edit-mode transient state
		setIsAddingWaypoints: (v) => setState("isAddingWaypoints", v),
		toggleIsAddingWaypoints: () => setState("isAddingWaypoints", (prev) => !prev),
		setActiveSegmentIdx: (idx) => setState("activeSegmentIdx", idx),

		// Display
		setShowLabels: (v) => setState("showLabels", v),
		toggleShowLabels: () => setState("showLabels", (prev) => !prev),

		// Mobile
		setIsMobile: (v) => setState("isMobile", v),
		setIsMobileMenuOpen: (v) => setState("isMobileMenuOpen", v),

		// Modals
		openModal: (modal) => setState("modals", modal, true),
		closeModal: (modal) => setState("modals", modal, false),
		toggleModal: (modal) => setState("modals", modal, (prev: boolean) => !prev),

		// Async ephemeral
		setShareUrl: (url) => setState("shareUrl", url),
		setErrorMessage: (msg) => setState("errorMessage", msg),

		// Reset
		resetApp: () => setState(produce(() => structuredClone(DEFAULT_STATE))),
	}

	const store: [AppState, AppActions] = [state, actions]

	return (
		<AppContext.Provider value={store}>
			{props.children}
		</AppContext.Provider>
	)
}
